import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { MovieCard } from "@/components/MovieCard";
import { Loading, ErrorBox, Empty } from "@/components/Common";

const TYPES = { movies: ["movie", "Movies"], "tv-shows": ["tv", "TV Shows"], anime: ["anime", "Anime"] };
const SORTS = [["popularity", "Popularity"], ["release", "Release date"], ["rating", "Community rating"], ["rating_count", "Most rated"], ["imdb", "IMDb rating"], ["title", "A–Z"]];

function Select({ label, k, options, params, set }) {
  return (
    <label className="flex flex-col gap-1 text-xs text-slate-400">
      {label}
      <select data-testid={`filter-${k}`} value={params.get(k) || ""} onChange={(e) => set(k, e.target.value)}
        className="rounded-lg border border-white/10 bg-[#12121A] px-3 py-2 text-sm text-slate-100 outline-none focus:border-amber-400">
        <option value="">Any</option>
        {options.map((o) => (Array.isArray(o) ? <option key={o[0]} value={o[0]}>{o[1]}</option> : <option key={o} value={o}>{o}</option>))}
      </select>
    </label>
  );
}

function Filters({ params, set, facets, showType }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="browse-filters">
      {showType && <Select label="Type" k="type" options={[["movie", "Movies"], ["tv", "TV Shows"], ["anime", "Anime"]]} params={params} set={set} />}
      <Select label="Genre" k="genre" options={facets.genres || []} params={params} set={set} />
      <Select label="Year" k="year" options={facets.years || []} params={params} set={set} />
      <Select label="Language" k="language" options={facets.languages || []} params={params} set={set} />
      <Select label="Country" k="country" options={facets.countries || []} params={params} set={set} />
      <Select label="Runtime" k="runtime_max" options={[["90", "Under 90 min"], ["120", "Under 2 hours"], ["150", "Under 2.5 hours"]]} params={params} set={set} />
      <Select label="Community rating" k="min_rating" options={[["3", "3★ +"], ["3.5", "3.5★ +"], ["4", "4★ +"], ["4.5", "4.5★ +"]]} params={params} set={set} />
      <Select label="IMDb rating" k="min_imdb" options={[["7", "7+"], ["8", "8+"]]} params={params} set={set} />
      <Select label="Platform" k="provider" options={(facets.providers || []).map((p) => p.name)} params={params} set={set} />
      <Select label="Availability" k="avail" options={[["subscription", "Subscription"], ["free", "Free"], ["rent", "Rent"], ["buy", "Buy"], ["cinema", "In cinemas"]]} params={params} set={set} />
      <Select label="Region" k="region" options={(facets.regions || []).map((r) => [r.code, r.name])} params={params} set={set} />
      <Select label="Sort by" k="sort" options={SORTS} params={params} set={set} />
    </div>
  );
}

export default function Browse({ kind }) {
  const { key } = useParams();
  const [params, setParams] = useSearchParams();
  const [facets, setFacets] = useState({});
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [open, setOpen] = useState(false);
  const [type, label] = TYPES[kind] || [null, null];
  const [section, setSection] = useState(null);

  useEffect(() => { api.get("/facets").then((r) => setFacets(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    setData(null); setErr(false);
    if (key) {
      api.get(`/sections/${key}`).then((r) => { setSection(r.data.section); setData({ items: r.data.items, total: r.data.items.length, pages: 1 }); }).catch(() => setErr(true));
      return;
    }
    const p = Object.fromEntries(params.entries());
    if (type && !params.has("type")) p.type = type;
    if (!p.type) delete p.type;
    api.get("/titles", { params: p }).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [params, key, type]);

  const heading = key ? section?.title || "Collection" : params.get("q") ? `Results for “${params.get("q")}”` : label || "Browse";
  useSeo({ title: heading, description: `Discover ${heading.toLowerCase()} with genuine community ratings and verified legal streaming links on Shivam Cine Core.` });
  const set = (k, v) => { const n = new URLSearchParams(params); v ? n.set(k, v) : n.delete(k); n.delete("page"); setParams(n); };
  const page = Number(params.get("page") || 1);
  const activeCount = [...params.keys()].filter((k) => !["page", "q", "sort"].includes(k) && params.get(k)).length;

  return (
    <div className="px-4 py-8 sm:px-6 lg:px-12" data-testid="browse-page">
      <nav className="mb-2 text-xs text-slate-500" aria-label="Breadcrumb"><Link to="/" className="hover:text-amber-400">Home</Link> / <span>{heading}</span></nav>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-5xl sm:text-6xl">{heading}</h1>
          {key && section?.note && <p className="mt-1 text-sm text-slate-400">{section.note}</p>}
        </div>
        {!key && (
          <div className="flex gap-2">
            {activeCount > 0 && <button onClick={() => setParams(params.get("q") ? { q: params.get("q") } : {})} className="flex items-center gap-1 rounded-full border border-white/10 px-4 py-2 text-xs" data-testid="clear-filters"><X className="h-3 w-3" />Clear</button>}
            <button data-testid="toggle-filters" onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 rounded-full border border-amber-400/40 px-4 py-2 text-sm text-amber-300 hover:bg-amber-400/10">
              <SlidersHorizontal className="h-4 w-4" />Filters {activeCount ? `(${activeCount})` : ""}
            </button>
          </div>
        )}
      </div>
      {!key && open && <div className="mt-6 rounded-2xl border border-white/5 bg-[#0F0F16] p-4"><Filters params={params} set={set} facets={facets} showType={!type} /></div>}
      {err ? <ErrorBox text="We couldn't load titles. Please try again." /> : !data ? <Loading /> : data.items.length === 0 ? (
        <div className="mt-10"><Empty title="No titles here yet" text={key ? "This row is still being curated — check back soon." : "Try removing a filter or searching for something else. We never show availability we haven't verified."} testid="browse-empty" /></div>
      ) : (
        <>
          <p className="mt-6 text-sm text-slate-500" data-testid="browse-total">{data.total} title{data.total === 1 ? "" : "s"}</p>
          <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {data.items.map((t) => <MovieCard key={t.id} t={t} size="fluid" />)}
          </div>
          {data.pages > 1 && (
            <div className="mt-10 flex items-center justify-center gap-3">
              <button disabled={page <= 1} onClick={() => set("page", page - 1)} className="rounded-full border border-white/10 px-4 py-2 text-sm disabled:opacity-40" data-testid="page-prev">Previous</button>
              <span className="text-sm text-slate-400">Page {page} of {data.pages}</span>
              <button disabled={page >= data.pages} onClick={() => set("page", page + 1)} className="rounded-full border border-white/10 px-4 py-2 text-sm disabled:opacity-40" data-testid="page-next">Next</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
